import { forwardRef } from "react";
import { inr, shortId, fmtDateTime, itemCount, itemLabel, mapsLink } from "@/lib/format";
import { LEGAL_NAME, WEBSITE } from "@/lib/brand";
import { QRCodeSVG } from "qrcode.react";

const Line = () => <div style={{ borderTop: "1px dashed #000", margin: "8px 0" }} />;

// Per-item add-ons / special requests (defensive across possible backend field names).
function itemExtras(it) {
  const out = [];
  const addons = it.add_ons || it.addons || it.extras || it.modifiers;
  if (Array.isArray(addons)) {
    addons.forEach((a) => out.push(typeof a === "string" ? a : a?.name || a?.label));
  } else if (typeof addons === "string" && addons.trim()) {
    out.push(addons.trim());
  }
  const note = it.note || it.notes || it.special || it.instructions;
  if (typeof note === "string" && note.trim()) out.push(note.trim());
  return out.filter(Boolean);
}

const Row = ({ left, right, bold, big }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      fontWeight: bold ? 800 : 500,
      fontSize: big ? "22px" : bold ? "18px" : "16px",
      lineHeight: 1.45,
    }}
  >
    <span style={{ paddingRight: 8 }}>{left}</span>
    <span style={{ whiteSpace: "nowrap" }}>{right}</span>
  </div>
);

const ThermalReceipt = forwardRef(function ThermalReceipt({ order, vendor, outlet }, ref) {
  if (!order) return <div id="receipt" className="print-doc" ref={ref} />;

  // Vendor bill: single TOTAL = sum of item line amounts (platform fee is baked into menu prices).
  const discount = Number(order.coupon_discount || 0) + Number(order.coins_discount || 0);
  const itemsTotal = (order.items || []).reduce(
    (s, it) => s + (Number(it.price) || 0) * (Number(it.quantity) || 1),
    0
  );
  const billTotal = Math.max(0, itemsTotal - discount);

  return (
    <div id="receipt" className="print-doc" ref={ref} style={{ fontFamily: "'JetBrains Mono', monospace", color: "#000" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontWeight: 900, fontSize: "24px" }}>
          {vendor?.shop_name || order.restaurant_name || "1APPONLY"}
        </div>
        {outlet?.address && (
          <div data-testid="receipt-shop-address" style={{ fontSize: "14px", fontWeight: 500, lineHeight: 1.3, marginTop: 2 }}>
            {outlet.address}
          </div>
        )}
        {(outlet?.city || vendor?.city) && (
          <div style={{ fontSize: "15px", fontWeight: 600 }}>
            {outlet?.city || vendor.city} - {outlet?.pincode || vendor?.pincode || ""}
          </div>
        )}
        {mapsLink(outlet) && (
          <div data-testid="receipt-shop-map" style={{ fontSize: "12px", fontWeight: 700, marginTop: 2, wordBreak: "break-all" }}>
            Map: {mapsLink(outlet)}
          </div>
        )}
        <div style={{ fontSize: "14px", marginTop: 3, fontWeight: 600 }}>1APPONLY</div>
      </div>

      <Line />
      <Row left="Order" right={shortId(order.id)} bold />
      <Row left="Date" right={fmtDateTime(order.created_at)} />
      {order.customer_name ? <Row left="Customer" right={order.customer_name} bold /> : null}
      {order.customer_phone ? <Row left="Phone" right={order.customer_phone} /> : null}
      {order.prep_time_min ? <Row left="Prep time" right={order.prep_time_min + " min"} /> : null}

      {/* Items table: Qty | Item | Amount */}
      <table style={{ width: "100%", borderCollapse: "collapse", margin: "10px 0", fontSize: "17px" }}>
        <thead>
          <tr style={{ borderBottom: "2px solid #000" }}>
            <th style={{ textAlign: "left", fontWeight: 900, padding: "0 6px 4px 0", whiteSpace: "nowrap" }}>Qty</th>
            <th style={{ textAlign: "left", fontWeight: 900, padding: "0 6px 4px 6px" }}>Item</th>
            <th style={{ textAlign: "right", fontWeight: 900, padding: "0 0 4px 6px", whiteSpace: "nowrap" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {(order.items || []).map((it, i) => (
            <tr key={`${it.menu_item_id || it.name}-${i}`}>
              <td style={{ textAlign: "left", fontWeight: 800, padding: "5px 6px 5px 0", verticalAlign: "top", whiteSpace: "nowrap" }}>{it.quantity || 1}</td>
              <td style={{ textAlign: "left", fontWeight: 800, padding: "5px 6px 5px 6px", verticalAlign: "top", lineHeight: 1.3 }}>
                {itemLabel(it)}
                {itemExtras(it).map((ex, k) => (
                  <div key={`${ex}-${k}`} style={{ fontSize: "13px", fontWeight: 500, lineHeight: 1.25 }}>» {ex}</div>
                ))}
              </td>
              <td style={{ textAlign: "right", fontWeight: 800, padding: "5px 0 5px 6px", verticalAlign: "top", whiteSpace: "nowrap" }}>{inr((it.price || 0) * (it.quantity || 1))}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {discount > 0 && <Row left="Discount" right={"-" + inr(discount)} />}
      <div style={{ borderTop: "2px solid #000", margin: "6px 0" }} />
      <Row left="Total" right={"= " + inr(billTotal)} bold big />
      <Line />

      <Row left="Payment" right={(order.payment_method || "-").toUpperCase()} />
      <Row left="Status" right={(order.payment_status || "-").toUpperCase()} />
      <Row left="Items" right={String(itemCount(order))} />

      {order.instructions ? (
        <>
          <Line />
          <div style={{ fontSize: "15px", fontWeight: 500 }}>
            <b>Note:</b> {order.instructions}
          </div>
        </>
      ) : null}

      {order.delivery_address ? (
        <>
          <Line />
          <div style={{ fontSize: "14px", fontWeight: 500 }}>
            <b>Deliver to:</b> {order.delivery_address}
          </div>
        </>
      ) : null}

      {mapsLink(outlet) && (
        <>
          <Line />
          <div data-testid="receipt-shop-qr" style={{ textAlign: "center" }}>
            <QRCodeSVG value={`https://${mapsLink(outlet)}`} size={110} level="M" marginSize={1} style={{ display: "block", margin: "0 auto" }} />
            <div style={{ fontSize: "12px", fontWeight: 700, marginTop: 2 }}>Scan for shop location</div>
          </div>
        </>
      )}

      <Line />
      <div style={{ textAlign: "center", fontSize: "16px", marginTop: 6, fontWeight: 600 }}>
        Thank you!
        <br />
        Powered by 1APPONLY
        <div style={{ fontSize: "11px", fontWeight: 500, marginTop: 2 }}>{LEGAL_NAME}</div>
        <div style={{ fontSize: "12px", fontWeight: 700, marginTop: 2 }}>{WEBSITE}</div>
      </div>
    </div>
  );
});

export default ThermalReceipt;
